import React, { useEffect, useState, useMemo, useCallback, useContext } from 'react';
import Layout from 'components/sales/layout/layout';
import { AppliedFiltersBar, TableSectionV2 } from 'components/UI';
import localization from 'assets/localization/localization';
import Images from 'assets/images/imageData';
import { BaseUrl } from 'contexts/baseUrl/baseUrl';
import { MobileActionMenu, DesktopActionMenu } from 'components/layout';
import ListFilters from 'components/sales/ListFilters/ListFilters';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getVendorEditRequestSentMessage, isApprovedVendorStatus } from '../vendorSubmissionMessages';
import './vendorstyle.css';

const INITIAL_FILTERS = { searchKey: '' };

const cell = (val) => (val != null && val !== '' ? String(val) : '—');

const VendorList = ({
  getVendorOnboardingList,
  getVendorOnboardedListSearch,
  vendorEditRequest,
  postSalesList,
}) => {
  const { baseUrl } = useContext(BaseUrl);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const status = queryParams.get('status');
  const isPendingList = status === 'pending';

  const [listData, setListData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterData, setFilterData] = useState(INITIAL_FILTERS);
  const [showFilter, setShowFilter] = useState(false);

  useEffect(() => {
    if (getVendorOnboardingList !== null) {
      (async () => {
        setIsLoading(true);
        if (isPendingList) {
          await getVendorOnboardingList(null, 1, 1);
        } else {
          await getVendorOnboardingList(null, 1);
        }
        setIsLoading(false);
      })();
    }
  }, [getVendorOnboardingList, isPendingList]);

  useEffect(() => {
    if (postSalesList?.data) {
      setListData(postSalesList.data);
      setPageCount(Math.ceil(postSalesList.total / postSalesList.per_page));
      if (postSalesList.current_page) {
        setCurrentPage(postSalesList.current_page);
      }
    } else {
      setListData([]);
      setPageCount(0);
    }
  }, [postSalesList]);

  const handleEditVendorRequest = useCallback(async (email, approvalStatus) => {
    if (!email) return;

    toast.warning('Request is sending, please wait...');
    const result = await vendorEditRequest({ email });
    if (result) {
      toast.success(
        getVendorEditRequestSentMessage(isApprovedVendorStatus(approvalStatus))
      );
    }
  }, [vendorEditRequest]);

  const buildRowActions = useCallback(
    (row) => {
      const actions = [];

      if (isPendingList) {
        actions.push({
          name: 'View Details',
          link: `${baseUrl}/approval_pending_vendor_details/${row.id}`,
        });
        return actions;
      }

      actions.push({
        name: 'View Details',
        link: `${baseUrl}/vendor_onboard_details/${row.id}`,
      });

      if (row.approval_status === '3') {
        actions.push(
          {
            name: 'Add Bank Detail',
            link: `${baseUrl}/add_vendor_bank_detail/${row.id}`,
          },
          {
            name: 'Add Invoice',
            link: `${baseUrl}/add_invoice_payment/${row.id}`,
          },
          {
            name: 'Add Advance Payment',
            link: `${baseUrl}/add_advance_invoice_payment/${row.id}`,
          }
        );
      }

      return actions;
    },
    [baseUrl, isPendingList]
  );

  const tableColumns = useMemo(() => {
    const columns = [];

    if (!isPendingList) {
      columns.push({
        key: 'vendor_code',
        label: 'Vendor Code',
        format: (val) => cell(val),
      });
    }

    columns.push(
      { key: 'name_of_firm', label: 'Firm Name', format: (val) => cell(val) },
      { key: 'contact_person_name', label: 'Contact Person', format: (val) => cell(val) },
      { key: 'contact_no', label: 'Contact No', format: (val) => cell(val) },
      { key: 'email', label: 'Mail Id', format: (val) => cell(val) },
      { key: 'type_of_firm', label: 'Firm Type', format: (val) => cell(val) },
      { key: 'approvalStatus', label: 'Status', format: (val) => cell(val) },
      { key: 'created_at', label: 'Requested Date', format: (val) => cell(val) }
    );

    if (!isPendingList) {
      columns.push({
        key: 'raise_request',
        label: 'Raise Request',
        format: (_, row) => {
          const requestStatus = row.request_status ?? '';
          const isApproved = requestStatus === 'Approved';

          if (isApproved) {
            return (
              <button
                type="button"
                className="btn btn-primary btn-sm vendor-listing-raise-request-btn"
                onClick={() => handleEditVendorRequest(row.email, row.approval_status)}
              >
                Edit Request
              </button>
            );
          }

          return (
            <button
              type="button"
              className="btn btn-primary btn-sm vendor-listing-raise-request-btn"
              disabled
            >
              {requestStatus || '—'}
            </button>
          );
        },
      });
    }

    columns.push({
      key: 'actions',
      label: 'Action',
      getActions: (row) => buildRowActions(row),
    });

    return columns;
  }, [isPendingList, buildRowActions, handleEditVendorRequest]);

  const appliedFilterLabels = useMemo(() => {
    const labels = [];
    const searchKey = filterData?.searchKey?.trim();
    if (searchKey) labels.push(`Vendor / Firm: ${searchKey}`);
    return labels;
  }, [filterData]);

  const filterToggle = () => {
    setShowFilter(!showFilter);
  };

  const fetchList = async (page, filters = filterData) => {
    const searchKey = filters?.searchKey?.trim();
    if (searchKey) {
      if (isPendingList) {
        await getVendorOnboardedListSearch(searchKey, page, 1);
      } else {
        await getVendorOnboardedListSearch(searchKey, page);
      }
      return;
    }
    if (isPendingList) {
      await getVendorOnboardingList(null, page, 1);
    } else {
      await getVendorOnboardingList(null, page);
    }
  };

  const headerBtns = () => {
    const actionsMenuItems = [
      {
        label: 'Add Vendor',
        to: `${baseUrl}/add_vendor_req`,
        icon: Images?.AddUser,
      },
      {
        label: localization.showFilters,
        onClick: filterToggle,
        icon: Images?.FilterW,
      },
    ];

    return (
      <div className="d-flex align-items-center flex-wrap gap-2 vendor-listing-header-actions">
        <MobileActionMenu items={actionsMenuItems} toggleLabel="Actions" />
        <DesktopActionMenu items={actionsMenuItems} toggleLabel="Actions" />
      </div>
    );
  };

  const applyFilter = async (appliedFilterData) => {
    setFilterData(appliedFilterData);
    setIsLoading(true);
    setCurrentPage(1);
    await fetchList(1, appliedFilterData);
    setIsLoading(false);
    filterToggle();
  };

  const removeFilter = async () => {
    setFilterData(INITIAL_FILTERS);
    setIsLoading(true);
    setCurrentPage(1);
    if (isPendingList) {
      await getVendorOnboardingList(null, 1, 1);
    } else {
      await getVendorOnboardingList(null, 1);
    }
    setIsLoading(false);
  };

  const onPageChange = async (val) => {
    const page = val.selected + 1;
    setCurrentPage(page);
    setIsLoading(true);
    await fetchList(page);
    setIsLoading(false);
  };

  const srNo = postSalesList?.from ?? (currentPage - 1) * (postSalesList?.per_page || 10) + 1;

  return (
    <Layout
      className="vendor-listing-page"
      title={
        isPendingList
          ? localization.vendorPendingApprovalRequests
          : localization.vendoronboard
      }
      Buttons={headerBtns}
      isLoading={isLoading}
      isGoback
      linkFlag
      linkUrl={`${baseUrl}/dashboard`}
      useNewHeaderLayout
    >
      <AppliedFiltersBar
        items={appliedFilterLabels}
        onClear={removeFilter}
        clearButtonLabel="Remove filter"
        clearButtonDisabled={isLoading}
      />

      <TableSectionV2
        hideHeader
        hideTitleAndStatusFilter
        className="vendor-listing-table-section"
        tableData={listData}
        tableColumns={tableColumns}
        tableSectionProps={{ srNo }}
        pageCount={pageCount}
        currentPage={currentPage}
        onPageChange={onPageChange}
        isLoading={isLoading}
        emptyMessage={
          isPendingList
            ? 'No approval pending requests found.'
            : 'No onboarded vendors found.'
        }
      />

      <ListFilters
        showFilter={showFilter}
        filterToggle={filterToggle}
        applyFilter={(val) => applyFilter(val)}
        vendorListingFilter
        listFilterData={{ appliedFilterValues: filterData }}
      />
    </Layout>
  );
};

export default VendorList;
